import React from "react";
import axios from "axios";

// AI Requests = "My Recent Translations" history list, moved out of the main Translate screen.

export default function AIRequests({ history, historyLoading, onRefresh, onDownload, onOpenConvertToCustomRequest, selectedConvertItem, onCloseConvertToCustomRequest, onConversionSubmitted, API_BASE, jwt }) {
  const [pemtMode, setPemtMode] = React.useState("full");
  const [formattingMode, setFormattingMode] = React.useState("professional");
  const [estimate, setEstimate] = React.useState(null);
  const [estimating, setEstimating] = React.useState(false);
  const [estimateError, setEstimateError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (typeof onRefresh === "function") onRefresh();
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    setEstimate(null);
    setEstimateError("");
    if (!selectedConvertItem || !selectedConvertItem.task_id || !API_BASE) return;

    let cancelled = false;
    const runEstimate = async () => {
      setEstimating(true);
      try {
        const res = await axios.post(
          `${API_BASE}/ai-requests/${selectedConvertItem.task_id}/estimate`,
          {
            pemt_mode: pemtMode,
            formatting: formattingMode === "professional",
            non_pdf_only: false,
          },
          { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
        );
        if (!cancelled) setEstimate(res.data);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Estimation failed. Please try again.";
        if (!cancelled) {
          setEstimate(null);
          setEstimateError(msg);
        }
      } finally {
        if (!cancelled) setEstimating(false);
      }
    };
    runEstimate();
    return () => { cancelled = true; };
  }, [selectedConvertItem, pemtMode, formattingMode, API_BASE, jwt]);

  const prettyLanguages = Array.isArray(selectedConvertItem?.pretty_languages) && selectedConvertItem.pretty_languages.length
    ? selectedConvertItem.pretty_languages
    : (Array.isArray(selectedConvertItem?.target_languages) ? selectedConvertItem.target_languages : []);
  const sourceFiles = Array.isArray(selectedConvertItem?.source_filenames) ? selectedConvertItem.source_filenames : [];
  const hasPdf = !!selectedConvertItem?.has_pdf;
  const pdfOnly = !!selectedConvertItem?.pdf_only;
  const fileCount = selectedConvertItem?.file_count || sourceFiles.length || 0;
  const numLangs = prettyLanguages.length || 0;
  const aiCost = Number(estimate?.cost || 0);
  const pemtCost = Number(estimate?.pemt_credits || 0);
  const formattingCost = Number(estimate?.formatting_credits || 0);
  const totalCost = Number(estimate?.grand_total_estimate || estimate?.total || (aiCost + pemtCost + formattingCost) || 0);
  const pemtRowTitle = pemtMode === "limited" ? "Estimated Limited Review" : "Estimated Full Human Post-Edit Review";
  const formattingProfessional = formattingMode === "professional";
  const formattingLabel = formattingProfessional
    ? "Professional File Formatting (matches English formatting)"
    : "Automated File Formatting Only (limited, may contain errors)";

  const formatMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const getEstimateRange = (total) => {
    const totalNum = Number(total || 0);
    if (!Number.isFinite(totalNum) || totalNum <= 0) return null;
    return { low: totalNum * 0.90, high: totalNum * 1.25 };
  };
  const formatEstimateRange = (total) => {
    const range = getEstimateRange(total);
    if (!range) return "—";
    return `${formatMoney(range.low)} - ${formatMoney(range.high)}`;
  };

  const formatDocxArtifactWarning = (files) => {
    const list = Array.isArray(files) ? files.filter(Boolean) : [];
    if (!list.length) return "";
    if (list.length === 1) {
      return `${list[0]} appears to contain PDF-to-Word conversion artifacts. Post-formatting time may be underestimated.`;
    }
    if (list.length === 2) {
      return `${list[0]} and ${list[1]} appear to contain PDF-to-Word conversion artifacts. Post-formatting time may be underestimated.`;
    }
    const head = list.slice(0, -1).join(", ");
    const tail = list[list.length - 1];
    return `${head}, and ${tail} appear to contain PDF-to-Word conversion artifacts. Post-formatting time may be underestimated.`;
  };


  const handleSubmitConversion = async () => {
    if (!selectedConvertItem?.task_id || !estimate || submitting) return;
    setSubmitting(true);
    setEstimateError("");
    try {
      await axios.post(
        `${API_BASE}/ai-requests/${selectedConvertItem.task_id}/convert`,
        {
          pemt_mode: pemtMode,
          formatting: formattingProfessional ? "true" : "false",
          estimate_json: estimate,
          estimated_word_count:
            estimate?.estimated_word_count ?? estimate?.word_count ?? estimate?.word_count_total ?? estimate?.total_words ?? null,
        },
        { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
      );
      if (typeof onConversionSubmitted === "function") onConversionSubmitted(selectedConvertItem.task_id);
    } catch (e) {
      setEstimateError(e?.response?.data?.detail || "Unable to submit custom request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (selectedConvertItem) {
    return (
      <div data-testid="ai-request-conversion-page" className="bg-white border rounded p-4 mt-2 mx-4">
        {onCloseConvertToCustomRequest && (
          <button
            data-testid="ai-request-conversion-back"
            type="button"
            onClick={onCloseConvertToCustomRequest}
            className="mb-4 text-sm underline font-bold"
            style={{ color: "#00a7e1" }}
          >
            ← Back to A.I. Requests
          </button>
        )}

        <h2 data-testid="ai-request-conversion-title" className="text-xl font-semibold mb-4">Convert A.I. Translation to Custom Request</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-sm text-gray-600 font-semibold mb-1">Translation Package</div>
            <div data-testid="ai-request-conversion-package" className="text-sm break-words">{selectedConvertItem.filename || "—"}</div>
          </div>
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-sm text-gray-600 font-semibold mb-1">Date</div>
            <div data-testid="ai-request-conversion-date" className="text-sm">
              {selectedConvertItem.created_at ? new Date(selectedConvertItem.created_at).toLocaleDateString() : "—"}
              {selectedConvertItem.created_at ? ` ${new Date(selectedConvertItem.created_at).toLocaleTimeString()}` : ""}
            </div>
          </div>
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-sm text-gray-600 font-semibold mb-1">Files</div>
            <div data-testid="ai-request-conversion-files" className="text-sm break-words">
              {sourceFiles.length === 0 ? "—" : sourceFiles.length === 1 ? sourceFiles[0] : `${sourceFiles.length} Files`}
            </div>
          </div>
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-sm text-gray-600 font-semibold mb-1">Languages</div>
            <div data-testid="ai-request-conversion-languages" className="text-sm break-words">
              {prettyLanguages.length === 0 ? "—" : prettyLanguages.join(", ")}
            </div>
          </div>
        </div>

        {selectedConvertItem.requirements ? (
          <div data-testid="ai-request-conversion-instructions" className="mb-4 border rounded p-3 bg-gray-50">
            <div className="text-sm text-gray-600 font-semibold mb-1">Additional Instructions</div>
            <div data-testid="ai-request-conversion-instructions-text" className="text-sm whitespace-pre-wrap">{selectedConvertItem.requirements}</div>
          </div>
        ) : null}

        <div className="mb-5">
          <div className="font-bold mb-2">Post-AI Translation Review (PEMT)</div>
          <div className="text-sm mb-2">Choose how much human review you want for the AI translation.</div>
          <div className="space-y-2">
            <label className="flex items-center gap-2"><input data-testid="ai-request-pemt-full" type="radio" name="pemtMode" checked={pemtMode === "full"} onChange={() => setPemtMode("full")} /> Full Human Review</label>
            <label className="flex items-center gap-2"><input data-testid="ai-request-pemt-limited" type="radio" name="pemtMode" checked={pemtMode === "limited"} onChange={() => setPemtMode("limited")} /> Limited Review (terminology verification + egregious errors)</label>
            <label className="flex items-center gap-2"><input data-testid="ai-request-pemt-none" type="radio" name="pemtMode" checked={pemtMode === "none"} onChange={() => setPemtMode("none")} /> No Review</label>
          </div>
        </div>

        <div className="mb-5">
          <div className="font-bold mb-2">Post-Translation Formatting</div>
          <div className="text-sm mb-2">Choose how we handle formatting and layout after translation.</div>
          <div className="space-y-2">
            <label className="flex items-center gap-2"><input data-testid="ai-request-formatting-professional" type="radio" name="formattingMode" checked={formattingMode === "professional"} onChange={() => setFormattingMode("professional")} /> Professional File Formatting (matches English formatting)</label>
            <label className="flex items-center gap-2"><input data-testid="ai-request-formatting-automated" type="radio" name="formattingMode" checked={formattingMode === "automated"} onChange={() => setFormattingMode("automated")} /> Automated File Formatting Only (limited, may contain errors)</label>
          </div>
        </div>

        {estimateError ? (
          <div data-testid="ai-request-conversion-error" className="text-red-600 mb-3 font-semibold">{estimateError}</div>
        ) : null}

        {/* PDF files are now included in post-AI translation review and formatting estimates. */}

        {estimate ? (
          <div data-testid="ai-request-estimate-panel" className="mt-4 border rounded p-3 bg-gray-50 mb-4">
            <div data-testid="ai-request-estimate-summary" className="font-semibold mb-1">
              Estimated cost is {formatEstimateRange(totalCost)}.
            </div>
            {hasPdf ? (
              <div data-testid="ai-request-pdf-warning" className="text-sm mb-1">
                Please note that scanned pages or embedded images in PDF files may not be included in this estimate.
              </div>
            ) : null}
            {estimate?.docx_artifact_warning ? (
              <div data-testid="ai-request-docx-warning" className="text-sm mb-1" style={{ color: "#dc2626" }}>
                {formatDocxArtifactWarning(estimate?.docx_artifact_warning_files)}
              </div>
            ) : null}
            {formattingMode === "automated" && pemtMode === "none" ? (
              <div data-testid="ai-request-formatting-warning" className="text-sm font-semibold mt-1" style={{ color: "#dc2626" }}>
                Post-translation formatting is <span className="underline">not</span> recommended if post AI-translation review is not done as the AI translation may cause formatting issues that cannot be repaired.
              </div>
            ) : null}
            <div className="text-sm font-semibold mt-1" style={{ color: "#dc2626" }}>
              You will not be charged by accepting the estimate. You will be able to approve/reject final costs before the translation is started.
            </div>
            <div className="text-xs mt-1 text-gray-700">
              To remove different costs, please modify your custom workflow.
            </div>
          </div>
        ) : null}

        <div className="flex justify-center mt-4">
          <button
            data-testid="ai-request-accept-estimate"
            type="button"
            onClick={handleSubmitConversion}
            disabled={estimating || submitting || !estimate}
            className={`text-white px-4 py-2 rounded ${(estimating || submitting || !estimate) ? "opacity-50 cursor-not-allowed" : "hover:opacity-90 font-bold"}`}
            style={{ backgroundColor: "#94c83d" }}
          >
            {estimating ? "Calculating Cost..." : submitting ? "Submitting..." : "Accept Estimate"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ai-requests-page" className="bg-white border rounded p-4 mt-2 mx-4">
      <div className="flex justify-between items-center mb-3">
        <span data-testid="ai-requests-title" className="font-semibold text-lg">My Recent A.I. Translations (Last 7 Days)</span>
        <button
          data-testid="ai-requests-refresh"
          onClick={onRefresh}
          className="text-white px-3 py-2 rounded hover:opacity-90"
          style={{ backgroundColor: "#94c83d" }}
        >
          Refresh
        </button>
      </div>

      {historyLoading ? (
        <div data-testid="ai-requests-loading">Loading...</div>
      ) : !history || history.length === 0 ? (
        <div data-testid="ai-requests-empty">No files found.</div>
      ) : (
        <div className="overflow-x-auto overflow-y-auto max-h-96">
          <table data-testid="ai-requests-table" className="w-full text-left text-sm min-w-[480px] border border-gray-200">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-200 px-3 py-2 whitespace-nowrap font-semibold align-top">Translation Package</th>
                <th className="border border-gray-200 px-3 py-2 whitespace-nowrap font-semibold align-top">Date</th>
                <th className="border border-gray-200 px-3 py-2 whitespace-nowrap font-semibold align-top">Details</th>
                <th className="border border-gray-200 px-3 py-2 whitespace-nowrap font-semibold align-top">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 10).map((item) => {
                const alreadyConverted = !!item.custom_request_submitted;
                const convertDisabled = alreadyConverted || !item.can_convert_to_custom;
                return (
                  <tr key={item.task_id} data-testid="ai-requests-row">
                    <td className="border border-gray-200 px-3 py-2 align-top truncate max-w-[140px]">{item.filename}</td>
                    <td className="border border-gray-200 px-3 py-2 align-top whitespace-nowrap">
                      {new Date(item.created_at).toLocaleDateString()}<br />
                      {new Date(item.created_at).toLocaleTimeString()}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 align-top truncate max-w-[220px]">
                      {item.file_count ? `${item.file_count} file${item.file_count > 1 ? "s" : ""}` : ""}
                      {item.lang_list
                        ? (() => {
                            let langs = [];
                            try {
                              langs = typeof item.lang_list === "string" ? JSON.parse(item.lang_list) : item.lang_list;
                            } catch {
                              langs = [];
                            }
                            return langs.length <= 3 ? (langs.length ? `, ${langs.join(", ")}` : "") : `, ${langs.length} langs`;
                          })()
                        : ""}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 align-top whitespace-nowrap">
                      <div className="flex flex-wrap gap-2">
                        <button
                          data-testid="ai-requests-download"
                          onClick={() => onDownload(item.task_id)}
                          className="text-white px-2 py-1 rounded hover:opacity-90"
                          style={{ backgroundColor: "#94c83d" }}
                        >
                          Download
                        </button>
                        <button
                          data-testid="ai-requests-convert"
                          onClick={() => {
                            if (!convertDisabled && typeof onOpenConvertToCustomRequest === "function") onOpenConvertToCustomRequest(item);
                          }}
                          disabled={convertDisabled}
                          className={`px-2 py-1 rounded ${convertDisabled ? "cursor-not-allowed text-white" : "text-white hover:opacity-90"}`}
                          style={{ backgroundColor: alreadyConverted ? "#9ca3af" : (convertDisabled ? "#9ca3af" : "#00a7e1") }}
                        >
                          {alreadyConverted ? "Custom Request Submitted" : "Convert to Custom Request"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}